import { computed, defineComponent, onMounted, onUnmounted, onUpdated, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { User } from '@/store/user/user.model';
import { UserAction, UserGetter } from '@/store/user/user.module';
import LoadingComponent from '@/components/loading/Loading.vue';
import UserFormComponent from './userForm/UserForm.vue';
import UserResetPasswordFormComponent from './userResetPasswordForm/UserResetPasswordForm.vue';

export default defineComponent({
  name: 'UserTableComponent',
  components: {
    LoadingComponent,
    UserFormComponent,
    UserResetPasswordFormComponent,
  },
  setup: () => {
    const swal = useSweetAlert();
    const store = useStore();
    const users = computed<User[]>(() => store.getters[UserGetter.ALL]);
    const isLoading = computed<boolean>(() => store.getters[UserGetter.STATUS] === 'init' || store.getters[UserGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const userForm = ref<InstanceType<typeof UserFormComponent>>();
    const userResetPasswordForm = ref<InstanceType<typeof UserResetPasswordFormComponent>>();
    const userId = ref('');

    onMounted(() => {
      store.dispatch(UserAction.FETCH)
        .then(() => initialDataTable());
    });

    onUpdated(() => initialDataTable());

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    const initialDataTable = () => {
      Promise.resolve(dtInstance.value?.destroy())
        .then(() => {
          const dt = datatable.value as HTMLTableElement;
          if (!$.fn.dataTable.isDataTable(dt)) {
            dtInstance.value = $(dt).DataTable({
              language: {
                emptyTable: 'ไม่พบข้อมูล',
                info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
                infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
                infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
                lengthMenu: 'แสดงข้อมูล _MENU_',
                paginate: {
                  next: 'ถัดไป',
                  previous: 'ก่อนหน้า',
                  first: 'First',
                  last: 'Last'
                },
                search: 'ค้นหา:',
                zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
              }
            });
          }
        });
    };

    const createClick = () => {
      Promise.resolve(userId.value = '')
        .then(() => userForm.value?.show());
    };

    const editClick = (id: string) => {
      Promise.resolve(userId.value = id)
        .then(() => userForm.value?.show());
    };

    const resetPasswordClick = (id: string) => {
      swal.confirm('รีเซ็ตรหัสผ่าน!', 'คุณต้องการรีเซ็ตรหัสผ่านเป็นค่าเริ่มต้นใช่หรือไม่?', 'ใช่ ดำเนินการเดี๋ยวนี้!',  '#dc3545')
        .then(async (result) => {
          if (result.isConfirmed) {
            Promise.resolve(userId.value = id)
              .then(() => userResetPasswordForm.value?.show());
          }
        });
    };

    const deleteUserClick = (id: string) => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(UserAction.DELETE, { id: id });
          }
        });
    };

    return {
      users,
      userId,
      userForm,
      userResetPasswordForm,
      isLoading,
      datatable,
      createClick,
      editClick,
      resetPasswordClick,
      deleteUserClick,
    };
  },
});
